const vertexShaderSource = `
  attribute vec4 aVertexPosition;
  attribute vec2 aTextureCoord;

  varying highp vec2 vTextureCoord;

  void main(void) {
    gl_Position = aVertexPosition;
    vTextureCoord = aTextureCoord;
  }
`;

const fragmentShaderSource = `
  varying highp vec2 vTextureCoord;

  uniform mediump float uAltitude;
  uniform mediump float uMax;
  uniform mediump float uOpacity;
  uniform sampler2D uSampler;

  mediump float LOWER_LIMIT = 1.0 / 255.0;

  void main(void) {
    gl_FragColor = texture2D(uSampler, vTextureCoord);
    mediump float red = gl_FragColor[0];

    if (red > uMax || red < LOWER_LIMIT) {
      gl_FragColor[3] = 0.0;
    } else {
      gl_FragColor[0] = 1.0;
      gl_FragColor[1] = 1.0;
      gl_FragColor[2] = 1.0;
      gl_FragColor[3] = red > uAltitude ? uOpacity : 1.0;
    }
  }
`;

// eslint-disable-next-line no-bitwise
const isPowerOf2 = (value) => !(value & (value - 1));

const loadShader = (gl, type, source) => {
  const shader = gl.createShader(type);

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(`An error occurred compiling the shaders:
    ${gl.getShaderInfoLog(shader)}`);
    gl.deleteShader(shader);
    return null;
  }

  return shader;
};

const initShaderProgram = (gl, vsSource, fsSource) => {
  const shaderProgram = gl.createProgram();
  gl.attachShader(shaderProgram, loadShader(gl, gl.VERTEX_SHADER, vsSource));
  gl.attachShader(shaderProgram, loadShader(gl, gl.FRAGMENT_SHADER, fsSource));
  gl.linkProgram(shaderProgram);

  if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
    console.error(`Unable to initialize the shader program:
    ${gl.getProgramInfoLog(shaderProgram)}`);
    return null;
  }

  return shaderProgram;
};

const initBuffers = (gl) => {
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  const positions = [
    -1.0, 1.0, 1.0,
    1.0, 1.0, 1.0,
    1.0, -1.0, 1.0,
    -1.0, -1.0, 1.0,
  ];

  // List of positions to build the shape.
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

  const textureCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, textureCoordBuffer);

  const textureCoordinates = [
    0.0, 0.0,
    1.0, 0.0,
    1.0, 1.0,
    0.0, 1.0,
  ];
  gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array(textureCoordinates),
      gl.STATIC_DRAW,
  );

  // Build the element array buffer; this specifies the indices
  // into the vertex arrays for each face's vertices.
  const indexBuffer = gl.createBuffer();
  gl.bindBuffer(
      gl.ELEMENT_ARRAY_BUFFER,
      indexBuffer,
  );

  const indices = [0, 1, 2, 0, 2, 3];
  gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      new Uint16Array(indices),
      gl.STATIC_DRAW,
  );

  return {
    position: positionBuffer,
    textureCoord: textureCoordBuffer,
    indices: indexBuffer,
  };
};

/**
 * Manages raster data in image with WebGL
 */
export default class WebGLRaster {
  /**
   * Construcor
   */
  constructor() {
    this.canvas = document.createElement('canvas');
    // this.canvas.style.position = 'fixed';
    // this.canvas.style.top = 0;
    // this.canvas.style.left = 0;
    // document.body.appendChild(this.canvas);

    this.gl = this.canvas.getContext('webgl', {
      alpha: true,
      antialias: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: true,
    });

    this.shaderProgram = initShaderProgram(
        this.gl,
        vertexShaderSource,
        fragmentShaderSource,
    );

    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: this.gl.getAttribLocation(
            this.shaderProgram,
            'aVertexPosition',
        ),
        textureCoord: this.gl.getAttribLocation(
            this.shaderProgram,
            'aTextureCoord',
        ),
      },
      uniformLocations: {
        uAltitude: this.gl.getUniformLocation(
            this.shaderProgram,
            'uAltitude',
        ),
        uMax: this.gl.getUniformLocation(
            this.shaderProgram,
            'uMax',
        ),
        uOpacity: this.gl.getUniformLocation(
            this.shaderProgram,
            'uOpacity',
        ),
        uSampler: this.gl.getUniformLocation(
            this.shaderProgram,
            'uSampler',
        ),
      },
    };

    this.buffers = initBuffers(this.gl);

    this.limit = 0.0;
    this.maxLimit = 0.0;
    this.maxLimitOpacity = 0.0;
  }

  /**
   * Set the limit of raster draw.
   * Do not draw red values greater than the limit.
   * @param {float} limit In range [0, 255].
   */
  setLimit(limit) {
    // Normalize limit for WebGL.
    this.limit = limit / 255;
  }

  /**
   * Set the limit of raster draw.
   * Do not draw red values greater than the limit.
   * @param {float} maxLimit In range [0, 255].
   * @param {float} opacity In range [0.0, 1.0].
   */
  setMaxLimit(maxLimit, opacity) {
    // Normalize limit for WebGL.
    this.maxLimit = maxLimit / 255;
    this.maxLimitOpacity = opacity / 255;
  }

  /**
   * Check if WebGL is not available
   * @return {bool} Has WebGL failed
   */
  isError() {
    return !this.gl;
  }

  /**
   * Prepares a texture of the image for WebGL.
   * @param {Image} image Image to be drawn.
   */
  setImage(image) {
    const { gl, canvas } = this;
    const texture = gl.createTexture();
    const level = 0;
    const internalFormat = gl.RGBA;
    const srcFormat = gl.RGBA;
    const srcType = gl.UNSIGNED_BYTE;

    canvas.width = image.width;
    canvas.height = image.height;

    gl.viewport(0, 0, canvas.width, canvas.height);

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
        gl.TEXTURE_2D,
        level,
        internalFormat,
        srcFormat,
        srcType,
        image,
    );

    // WebGL1 has different requirements for power of 2 images
    if (isPowerOf2(image.width) && isPowerOf2(image.height)) {
      gl.generateMipmap(gl.TEXTURE_2D);
    } else {
      // Turn of mips and set wrapping to clamp to edge
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }

    this.texture = texture;
  }

  /**
   * Clear webgl.
   */
  clear() {
    const { gl } = this;
    gl.clearColor(0.0, 0.0, 0.0, 0.0);
    gl.clearDepth(1.0); // Clear everything
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL); // Near things obscure far things
    // eslint-disable-next-line no-bitwise
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  }

  /**
   * Update WebGL image by drawing.
   */
  update() {
    const {
      buffers,
      gl,
      limit,
      maxLimit,
      maxLimitOpacity,
      programInfo,
      texture,
    } = this;

    this.clear();

    // The positions from the position
    // buffer into the vertexPosition attribute
    {
      const numComponents = 3;
      const type = gl.FLOAT;
      const normalize = false;
      const stride = 0;
      const offset = 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.position);
      gl.vertexAttribPointer(
          programInfo.attribLocations.vertexPosition,
          numComponents,
          type,
          normalize,
          stride,
          offset,
      );
      gl.enableVertexAttribArray(
          programInfo.attribLocations.vertexPosition,
      );
    }

    // The texture coordinates from the texture coordinate buffer
    // into the textureCoord attribute.
    {
      const numComponents = 2;
      const type = gl.FLOAT;
      const normalize = false;
      const stride = 0;
      const offset = 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.textureCoord);
      gl.vertexAttribPointer(
          programInfo.attribLocations.textureCoord,
          numComponents,
          type,
          normalize,
          stride,
          offset,
      );
      gl.enableVertexAttribArray(
          programInfo.attribLocations.textureCoord,
      );
    }

    // Indices to use to index the vertices
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.indices);
    gl.useProgram(programInfo.program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // Tell the shader we bound the texture to texture unit 0
    gl.uniform1i(programInfo.uniformLocations.uSampler, 0);
    gl.uniform1f(programInfo.uniformLocations.uAltitude, limit);
    gl.uniform1f(programInfo.uniformLocations.uMax, maxLimit);
    gl.uniform1f(programInfo.uniformLocations.uOpacity, maxLimitOpacity);

    {
      const vertexCount = 6;
      const type = gl.UNSIGNED_SHORT;
      const offset = 0;
      gl.drawElements(gl.TRIANGLES, vertexCount, type, offset);
    }
  }
}
